import IImage from '@app/types/data/base/IImage';
import IMapLabel, { IMapLabelWithAttributesAndBounds } from '@app/types/data/base/IMapLabel';
import PptxGenJS from 'pptxgenjs';
import { getBase64ImageFromSvgElement, getSvgElementFromSvgString } from '@app/services/export/ppt/utils/imageUtils';
import { createImageForSlide, logoImageOptions } from '@app/services/export/ppt/utils/layoutUtils';
import { colors } from '@app/services/export/ppt/variables/constants';
import { inchToPx, pxToIn } from '@app/services/export/ppt/utils/formattingUtils';
import { getPinAttributes, getRelativeBoundsTo } from '@components/007-Map/mapUtils';
import { copy, copyMini, copySmallBold } from '@app/services/export/ppt/variables/typography';
import ImageProps = PptxGenJS.ImageProps;
import TextPropsOptions = PptxGenJS.TextPropsOptions;
import SlideMasterProps = PptxGenJS.SlideMasterProps;

interface IProps {
	map: string;
	image: IImage;
	labels?: IMapLabel[];
	showCover?: boolean;
	visibleSvgGroup?: string;
	ignoreImage?: boolean;
}

interface IPosition {
	x: number;
	y: number;
	w: number;
	h: number;
}

export class Map {
	protected slide: PptxGenJS.Slide;

	protected props: IProps;

	protected position: IPosition;

	public constructor(slide: PptxGenJS.Slide, props: IProps, position: IPosition) {
		this.slide = slide;
		this.props = props;
		this.position = position;
	}

	public create(): void {
		if (!this.props.ignoreImage) {
			this.createMapImage();
			this.createMapOverlay();
		}
		this.createMapSvgGraphic();
		this.createMapLabelsForSlide();
	}

	private createMapImage(): void {
		createImageForSlide(this.slide, this.props.image, {
			x: this.position.x,
			y: this.position.y,
			destinationWidth: this.position.w,
			destinationHeight: this.position.h,
			type: 'cover'
		});
	}

	private createMapOverlay(): void {
		if (!this.props.showCover) return;

		this.slide.addShape('rect', {
			...this.position,
			fill: { color: colors.blue, transparency: 40 }
		});
	}

	private createMapSvgGraphic(): void {
		const svgDimension = this.getSvgDimensions();
		const svgElement = this.getRelevantSvgGraphicFromMapString();

		if (!svgDimension) return;

		const { w, h } = svgDimension;

		this.slide.addImage({
			data: getBase64ImageFromSvgElement(svgElement),
			x: this.position.x,
			y: this.position.y,
			w: pxToIn(w),
			h: pxToIn(h),
			sizing: { type: 'cover', w: this.position.w, h: this.position.h }
		});
	}

	private getSvgDimensions(): { w: number; h: number } | null {
		const svgElement = this.getRelevantSvgGraphicFromMapString();

		if (!svgElement) {
			return null;
		}

		let w = svgElement.getAttribute('width');
		let h = svgElement.getAttribute('height');
		const viewBox = svgElement.getAttribute('viewBox');

		if (!w || !h) {
			// if the svg graphic has no width or height, we could try viewBox
			if (viewBox) {
				[,,w,h] = viewBox.split(' ');
			} else {
				console.warn('Map SVG has no width or height');
				return null;
			}
		}


		return {
			w: Number(w.replace('px', '')),
			h: Number(h.replace('px', ''))
		};
	}

	private getRelevantSvgGraphicFromMapString(): SVGElement {
		const svgElement = getSvgElementFromSvgString(this.props.map) as SVGElement;
		const id = this.props.visibleSvgGroup;

		return id ? this.reduceSvgToGroup(svgElement, id) : svgElement;
	}

	private reduceSvgToGroup(svgElement: SVGElement, groupId: string): SVGElement {
		const mapCLone = svgElement.cloneNode(true) as SVGElement;

		const svgGroups = mapCLone.querySelectorAll('[id^="group_"]');

		svgGroups.forEach(svgGroup => {
			if (svgGroup.id !== `group_${groupId}`) {
				svgGroup.remove();
			}
		});

		return mapCLone;
	}

	private createMapLabelsForSlide(): void {
		const labelsForGroup = this.getLabelsWithAttributesAndBounds();

		labelsForGroup.forEach(item => {
			const {hideInViewports} = item.attributes;

			if (hideInViewports && hideInViewports.includes('l')) {
				return;
			}

			if (item.type === 'location') {
				this.placeLocationLabel(item);
			}

			if (item.type === 'meta') {
				this.placeMetaLabel(item);
			}

			if (item.type === 'train') {
				this.placeTrainLabel(item);
			}
		});
	}

	private placeLocationLabel(item: IMapLabelWithAttributesAndBounds): void {
		// defaults
		const locationTextOptions: TextPropsOptions = {
			...copySmallBold,
			color: colors.white,
			margin: 0,
			w: 2.6,
			h: 0.15,
			glow: {
				color: '000000',
				size: 15,
				opacity: 0.35
			}
		};

		let imageOptions: ImageProps | null = null;
		const imageDiameter = 0.6;
		const hOffset = 0.07;
		const x = pxToIn(item.bounds.x);
		const y = pxToIn(item.bounds.y);
		const w = pxToIn(item.bounds.w);
		const h = pxToIn(item.bounds.h);

		if (item.image && item.image.width && item.image.height && item.attributes.imageAlignment) {
			imageOptions = {
				path: item.image.src,
				w: pxToIn(item.image.width),
				h: pxToIn(item.image.height),
				rounding: true,
				sizing: { type: 'cover', w: imageDiameter, h: imageDiameter }
			};
		}

		if (item.attributes.alignmentX === 'r') {
			const offset = item.attributes.alignmentY === 'c' ? hOffset : 0;

			locationTextOptions.x = x + w + offset;
			locationTextOptions.align = 'left';

			if (imageOptions) {
				imageOptions.x = x + w;
			}
		}

		if (item.attributes.alignmentX === 'l') {
			const offset = item.attributes.alignmentY === 'c' ? hOffset : 0;

			locationTextOptions.x = x - (locationTextOptions.w as number) - offset;
			locationTextOptions.align = 'right';

			if (imageOptions) {
				imageOptions.x = x - imageDiameter;
			}
		}

		if (item.attributes.alignmentX === 'c') {
			locationTextOptions.x = x - (locationTextOptions.w as number) / 2 + w / 2;
			locationTextOptions.align = 'center';

			if (imageOptions) {
				imageOptions.x = x - imageDiameter / 2 + w / 2;
			}
		}

		if (item.attributes.alignmentY === 't') {
			(locationTextOptions.y as number) = y - (locationTextOptions.h as number);
		}

		if (item.attributes.alignmentY === 'b') {
			(locationTextOptions.y as number) = y + h;
		}

		if (item.attributes.alignmentY === 'c') {
			(locationTextOptions.y as number) = y - (locationTextOptions.h as number) / 2 + h / 2;
		}

		if (imageOptions) {
			if (item.attributes.imageAlignment === 'b') {
				imageOptions.y = (locationTextOptions.y as number) + (locationTextOptions.h as number);
			}

			if (item.attributes.imageAlignment === 't') {
				imageOptions.y = (locationTextOptions.y as number) - imageDiameter;
			}
		}

		if (item.attributes.fontStyle.toLocaleUpperCase() === 'L') {
			locationTextOptions.fontSize = copy.fontSize;
		}

		this.slide.addText(item.label.toLocaleUpperCase(), locationTextOptions);

		if (imageOptions) {
			this.slide.addImage(imageOptions);
		}
	}

	private placeMetaLabel(item: IMapLabelWithAttributesAndBounds): void {
		// defaults
		const metaTextOptions: TextPropsOptions = {
			...copyMini,
			charSpacing: 0.1,
			color: colors.blue,
			align: 'center',
			shape: 'roundRect',
			rectRadius: 0.03,
			lineSpacing: 0,
			margin: 0,
			w: 0.85,
			h: 0.15,
			fill: {
				color: Map.validatedRelatedPathColor(item.attributes.relatedSvgPathColor)
			}
		};

		const hOffset = 0.07;
		const x = pxToIn(item.bounds.x);
		const y = pxToIn(item.bounds.y);
		const w = pxToIn(item.bounds.w);
		const h = pxToIn(item.bounds.h);

		if (item.attributes.alignmentX === 'r') {
			const offset = item.attributes.alignmentY === 'c' ? hOffset : 0;
			metaTextOptions.x = x + w + offset;
		}

		if (item.attributes.alignmentX === 'l') {
			const offset = item.attributes.alignmentY === 'c' ? hOffset : 0;
			metaTextOptions.x = x - (metaTextOptions.w as number) - offset;
		}

		if (item.attributes.alignmentX === 'c') {
			metaTextOptions.x = x - (metaTextOptions.w as number) / 2 + w / 2;
		}

		if (item.attributes.alignmentY === 't') {
			metaTextOptions.y = y - (metaTextOptions.h as number);
		}

		if (item.attributes.alignmentY === 'b') {
			metaTextOptions.y = y + h;
		}

		if (item.attributes.alignmentY === 'c') {
			metaTextOptions.y = y - (metaTextOptions.h as number) / 2 + h / 2;
		}

		this.slide.addText(item.label.toLocaleUpperCase(), metaTextOptions);
	}

	private placeTrainLabel(item: IMapLabelWithAttributesAndBounds): void {
		let imageOptions: ImageProps | null = null;
		const imageWidth = 0.4;
		const imageHeight = 0.6;

		const x = pxToIn(item.bounds.x);
		const y = pxToIn(item.bounds.y);
		const w = pxToIn(item.bounds.w);
		const h = pxToIn(item.bounds.h);

		if (item.image && item.image.width && item.image.height) {
			imageOptions = {
				path: item.image.src,
				w: pxToIn(item.image.width),
				h: pxToIn(item.image.height),
				sizing: { type: 'contain', w: imageWidth, h: imageHeight }
			};

			if (item.attributes.alignmentX === 'r') {
				imageOptions.x = x + w;
			}

			if (item.attributes.alignmentX === 'l') {
				imageOptions.x = x - imageWidth;
			}

			if (item.attributes.alignmentX === 'c') {
				imageOptions.x = x - imageWidth / 2 + w / 2;
			}

			if (item.attributes.alignmentY === 't') {
				imageOptions.y = y - imageHeight;
			}

			if (item.attributes.alignmentY === 'b') {
				imageOptions.y = y + h;
			}

			if (item.attributes.alignmentY === 'c') {
				imageOptions.y = y - imageHeight / 2 + h / 2;
			}

			if (imageOptions) {
				this.slide.addImage(imageOptions);
			}
		}
	}

	private static validatedRelatedPathColor(color?: string): string {
		if (color === '#FFF' || !color) {
			return colors.white;
		}

		return color.replace('#', '');
	}

	private getLabelsWithAttributesAndBounds(): IMapLabelWithAttributesAndBounds[] {
		const svgMap = this.getRelevantSvgGraphicFromMapString();
		const generatedLabels: IMapLabelWithAttributesAndBounds[] = [];

		if (this.props.labels) {
			this.props.labels.forEach((label: IMapLabel) => {
				const svgPin: Element | null = svgMap.querySelector(`[id^='${label.type}_${label.svgId}_']`);

				if (svgPin) {
					let relatedSvgPath: Element | null = null;

					if (label.type === 'train' || label.type === 'meta') {
						relatedSvgPath = svgMap.querySelector(`[id='track_${label.svgId}']`);
					}

					const attributes = getPinAttributes(svgPin, relatedSvgPath);
					const bounds = this.getPinBounds(svgPin, svgMap);

					if (bounds && this.isInsideVisibleSection(bounds.x, bounds.y)) {
						generatedLabels.push({ ...label, attributes, bounds });
					}
				}
			});
		}

		return generatedLabels;
	}

	private getPinBounds = (
		svgPin: Element,
		svgMap: SVGElement
	): { x: number; y: number; w: number; h: number } | null => {
		const domHelper = this.createDomPositionHelper(svgMap);

		if (!domHelper) {
			return null;
		}

		const relativeBoundsToParent = getRelativeBoundsTo(svgPin, domHelper);

		const data = {
			x: relativeBoundsToParent.left,
			y: relativeBoundsToParent.top,
			w: svgPin.getBoundingClientRect().width,
			h: svgPin.getBoundingClientRect().height
		};

		domHelper.remove();

		return data;
	};

	private isInsideVisibleSection(x: number, y: number): boolean {
		const xInch = pxToIn(x);
		const yInch = pxToIn(y);

		return !(
			yInch > this.position.y + this.position.h ||
			yInch < this.position.y ||
			xInch > this.position.x + this.position.w ||
			xInch < this.position.x
		);
	}

	private createDomPositionHelper = (svgMap: SVGElement): HTMLDivElement | null => {
		const div = document.createElement('div');
		const svgDimension = this.getSvgDimensions();
		const localSvgMap = svgMap;

		if (!svgDimension) return null;

		// svg should cover the area and maintain its aspect ratio
		const scale = Math.max(this.position.w / pxToIn(svgDimension.w), this.position.h / pxToIn(svgDimension.h));

		div.classList.add('ppt-export-map-helper');
		div.style.width = `${inchToPx(this.position.w)}px`;
		div.style.height = `${inchToPx(this.position.h)}px`;
		localSvgMap.style.width = `${svgDimension.w * scale}px`;
		localSvgMap.style.height = `${svgDimension.h * scale}px`;
		div.appendChild(localSvgMap);
		document.body.appendChild(div);
		return div;
	};

	/**
	 * use masterSlide for performance gains, as image has to be encoded only once
	 * https://gitbrent.github.io/PptxGenJS/docs/masters/#slide-master-example
	 */
	public static createMapMasterObjectsForImage = (image: IImage, w: number, h: number): SlideMasterProps['objects'] => {
		if (!image || !image.src) {
			console.warn('No image src given for map!');
			return undefined;
		}

		return [
			{
				image: {
					path: /\.(jpg|png|svg)$/.test(image.src) ? image.src : `${image.src}.jpg`,
					x: 0,
					y: 0,
					w: image.width,
					h: image.height,
					sizing: {
						type: 'cover',
						w,
						h
					}
				}
			},
			{
				rect: {
					w,
					h,
					x: 0,
					y: 0,
					fill: { color: colors.blue, transparency: 40 }
				}
			},
			{
				image: logoImageOptions
			}
		];
	};
}
